import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { fetchAccounts, fetchCategories } from "@/lib/queries";
import { fetchTemplates, type RecurringTemplate } from "@/lib/recurring";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Pause, Play, Trash2, Calendar } from "lucide-react";
import { RecurringTemplateDialog } from "@/components/RecurringTemplateDialog";
import { useFormatCurrency } from "@/hooks/useFormatCurrency";
import { toast } from "sonner";
import { toastError } from "@/lib/errors";
import { accountTypeLabel } from "@/lib/accountDisplay";
import { useAppStore } from "@/store/useAppStore";
import { fetchMembers } from "@/lib/queries";
import { accountsQueryKey } from "@/lib/authScopedQueryKeys";

export const Route = createFileRoute("/_app/recurring")({ component: RecurringPage });

function RecurringPage() {
  const formatCurrency = useFormatCurrency();
  const { profile } = useProfile();
  const hh = profile?.household_id;
  const profileId = profile?.id;
  const qc = useQueryClient();
  const tplQ = useQuery({ queryKey: ["recurring-templates", hh], queryFn: () => fetchTemplates(hh!), enabled: !!hh });
  const accQ = useQuery({ queryKey: accountsQueryKey(hh ?? "", profileId ?? ""), queryFn: () => fetchAccounts(hh!), enabled: !!hh && !!profileId });
  const catQ = useQuery({ queryKey: ["categories", hh], queryFn: () => fetchCategories(hh!), enabled: !!hh });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<RecurringTemplate | null>(null);

  const memQ = useQuery({ queryKey: ["members", hh], queryFn: () => fetchMembers(hh!), enabled: !!hh });
  const { viewMode } = useAppStore();
  const isCouple = (memQ.data?.length ?? 1) > 1;

  const accMap = new Map((accQ.data ?? []).map((a) => [a.id, a]));
  const catMap = new Map((catQ.data ?? []).map((c: any) => [c.id, c]));

  async function togglePause(t: RecurringTemplate) {
    const { error } = await supabase.from("recurring_templates" as any).update({ is_active: !t.is_active }).eq("id", t.id);
    if (error) return toastError(error);
    qc.invalidateQueries({ queryKey: ["recurring-templates"] });
    toast.success(t.is_active ? "Pausada" : "Reactivada");
  }
  async function remove(t: RecurringTemplate) {
    if (!confirm(`Eliminar la plantilla "${t.name}"? Los pendientes futuros se borrarán; los movimientos ya aprobados se conservan.`)) return;
    const { error } = await supabase.from("recurring_templates" as any).delete().eq("id", t.id);
    if (error) return toastError(error);
    qc.invalidateQueries({ queryKey: ["recurring-templates"] });
    qc.invalidateQueries({ queryKey: ["recurring-occurrences"] });
    toast.success("Plantilla eliminada");
  }

  const allItems = tplQ.data ?? [];
  const items = isCouple
    ? allItems.filter((t) => {
        if (viewMode === "household") return t.visibility === "shared";
        if (viewMode === "personal") return t.visibility === "private" && t.owner_id === profile?.id;
        return true;
      })
    : allItems;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Movimientos recurrentes</h1>
          <p className="text-sm text-muted-foreground">Plantillas para gastos e ingresos que se repiten cada mes (suscripciones, alquiler, sueldos…).</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }}><Plus className="mr-2 h-4 w-4" /> Nueva plantilla</Button>
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Aún no creaste plantillas. Empezá con tu primera suscripción.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {items.map((t) => {
            const acc = accMap.get(t.account_id);
            const cat: any = catMap.get(t.category_id);
            return (
              <Card key={t.id} className={!t.is_active ? "opacity-60" : ""}>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {t.name}
                    {!t.is_active && <Badge variant="outline" className="text-[10px]">Pausada</Badge>}
                  </span>
                  <span className="text-sm font-bold">{formatCurrency(Number(t.amount_estimated), t.currency)}</span>
                </CardTitle></CardHeader>
                <CardContent className="space-y-2 pt-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="gap-1"><Calendar className="h-3 w-3" /> Día {t.day_of_month}</Badge>
                    <Badge variant="outline">{t.type === "expense" ? "Gasto" : "Ingreso"}</Badge>
                    <Badge variant="outline">{t.visibility === "shared" ? "🏠 Hogar" : "🧍 Personal"}</Badge>
                  </div>
                  <div className="text-xs">
                    {acc && <><span className="font-medium">{acc.name}</span> <span className="text-muted-foreground">· {accountTypeLabel(acc.type)}</span> · </>}
                    {cat && <span style={{ color: cat.color }}>● {cat.name}</span>}
                  </div>
                  {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                  <div className="flex gap-1 pt-2">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => togglePause(t)}>
                      {t.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(t)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <RecurringTemplateDialog open={open} onOpenChange={(b) => { setOpen(b); if (!b) setEditing(null); }} editing={editing} />
    </div>
  );
}
